import { FC, PropsWithChildren, useMemo } from 'react';

import { useFavicon } from 'shared/hooks/useFavicon';
import { useGetFormattedSettings } from 'shared/hooks/useGetFormattedSettings';
import { useDocumentTitle } from 'shared/hooks/useDocumentTitle';
import { Preview } from 'shared/components/Layout';
import { Error } from 'shared/components/Error';
import { Maintenance } from 'shared/components/Maintenance';
import { ErrorType } from 'shared/utils/axiosInstance';
import { InspiredLibrariesApiExceptionHandlingErrorResponse } from 'shared/models/payments';
import { useGetAccessibleAccounts } from 'shared/hooks/payment-api/accessible-accounts-endpoint';

import SettingsContext from './Settings.context';
import { useAuth } from '../Auth';

const SettingsProvider: FC<PropsWithChildren> = ({ children }) => {
  const { error, isLoading: authLoading, isLoggedIn } = useAuth();
  const {
    favIconUrl,
    schoolName,
    isLoading: settingsLoading,
    error: settingsError,
    isSiteAvailable,
    scheduleMaintenance
  } = useGetFormattedSettings();
  const { isLoading: accessibleAccountsIsLoading, error: accessibleAccountsError } =
    useGetAccessibleAccounts({ query: { enabled: isLoggedIn, useErrorBoundary: false } });

  useFavicon(favIconUrl);
  useDocumentTitle(schoolName);

  const content = useMemo(() => {
    if (settingsError || error || accessibleAccountsError) {
      const combinedError = (settingsError ||
        error ||
        accessibleAccountsError) as ErrorType<InspiredLibrariesApiExceptionHandlingErrorResponse>;

      return <Error error={combinedError} />;
    }

    if (!isSiteAvailable) {
      return (
        <Maintenance
          startDate={scheduleMaintenance?.startDate}
          endDate={scheduleMaintenance?.endDate}
        />
      );
    }

    if (authLoading || settingsLoading || accessibleAccountsIsLoading || !isLoggedIn) {
      return <Preview />;
    }

    return children;
  }, [
    settingsError,
    error,
    isSiteAvailable,
    authLoading,
    settingsLoading,
    isLoggedIn,
    children,
    scheduleMaintenance?.startDate,
    scheduleMaintenance?.endDate,
    accessibleAccountsIsLoading,
    accessibleAccountsError
  ]);

  return <SettingsContext.Provider value={null}>{content}</SettingsContext.Provider>;
};

export default SettingsProvider;
